import type {
  Attachment,
  ChangeLogChangeType,
  ChangeLogEntityType,
  CreateChangeLogMutation,
  CreateChangeLogMutationVariables,
  CreateMessageDeliveryLogMutation,
  CreateMessageDeliveryLogMutationVariables,
  DeliveredAttachmentDownloadPayload,
  DeliveredMessages,
  DeliveredMessagesAttachments,
  DownloadDeliveredMessageAttachmentsMutation,
  DownloadDeliveredMessageAttachmentsMutationVariables,
  EffectivePlatformLimits,
  Message,
  ModelSortDirection,
  Recipient,
} from "../API";
import type {
  DeliveredMessageListItem,
  LegacyMessageListItem,
} from "../types/message";

import { graphqlUserPool } from "../utilities/graphqlUserPool";

// ✅ These are your custom GraphQL documents already in utilities.ts
import {
  deliveredMessagesByFromUserProfileIdDocument,
  getMessageByIdWithBodyHydrated,
  listAttachmentsWithMessages,
  listMessagesWithRecipientsAndAttachments,
  listRecipientsWithMessages,
} from "./customQueries";

// ✅ Delivered query is from Amplify-generated graphql/queries
import {
  attachmentsByDeliveredMessageId,
  deliveredMessagesByRecipientEmail,
  getDeliveredMessageByIdWithBody,
  getMyEffectivePlatformLimits,
  getUser,
} from "./queries";
import {
  createChangeLog,
  createMessageDeliveryLog,
  downloadDeliveredMessageAttachments,
  updateDeliveredMessages,
} from "./mutations";
import { BillingPlan, getBillingPlan } from "../utilities/billing";
import { logToConsole } from "../utilities/utilities";
/** ---------------- User's Plan ---------------- */

export type UserSettings = {
  minBodyWordCount: number;
};

export async function fetchUserSettingsByUserId(
  userId: string,
): Promise<UserSettings> {
  const uid = userId?.trim();
  if (!uid) {
    return { minBodyWordCount: 25 };
  }

  const data: any = await graphqlUserPool({
    query: getUser,
    variables: { id: uid },
  });

  const minBodyWordCount = Number(
    data?.getUser?.UserPreferences?.minBodyWordCount ?? 25,
  );

  return {
    minBodyWordCount:
      Number.isFinite(minBodyWordCount) && Number.isInteger(minBodyWordCount)
        ? minBodyWordCount
        : 25,
  };
}

export async function fetchUserPlanByUserId(
  userId: string,
): Promise<BillingPlan | null> {
  const uid = userId?.trim();
  if (!uid) return null;

  const data: any = await graphqlUserPool({
    query: getUser,
    variables: { id: uid },
  });

  const user = data?.getUser;
  const planId = (user?.userPlanId ?? "").trim();
  if (!planId) return null;

  return await getBillingPlan(planId);
}

export type LatestLegalAcceptance = {
  version: string;
  acceptedAt: string;
};

// Not yet in Amplify-generated ./queries (added alongside the LegalAcceptance
// schema type, pending `amplify push` + codegen). Defined inline here, mirroring
// how createLoginEvent is hand-inlined in loginEvents.ts for the same reason.
const legalAcceptancesByUserQuery = /* GraphQL */ `query LegalAcceptancesByUser(
  $userId: ID!
  $sortDirection: ModelSortDirection
  $limit: Int
) {
  legalAcceptancesByUser(
    userId: $userId
    sortDirection: $sortDirection
    limit: $limit
  ) {
    items {
      id
      version
      acceptedAt
      __typename
    }
    __typename
  }
}`;

export async function fetchLatestLegalAcceptance(
  userId: string,
): Promise<LatestLegalAcceptance | null> {
  const uid = userId?.trim();
  if (!uid) return null;

  const data: any = await graphqlUserPool({
    query: legalAcceptancesByUserQuery,
    variables: {
      userId: uid,
      sortDirection: "DESC" as ModelSortDirection,
      limit: 1,
    },
  });

  const latest = data?.legalAcceptancesByUser?.items?.[0];
  return latest ? { version: latest.version, acceptedAt: latest.acceptedAt } : null;
}

export async function fetchEffectivePlatformLimitsByUserId(
  userId: string,
): Promise<EffectivePlatformLimits | null> {
  const uid = userId?.trim();
  if (!uid) return null;

  const data: any = await graphqlUserPool({
    query: getMyEffectivePlatformLimits,
  });

  return data?.getMyEffectivePlatformLimits ?? null;
}

/** ---------------- Legacy Messages (all pages) ---------------- */

type ListMessagesResponse = {
  listMessages: {
    items: any[];
    nextToken?: string | null;
  };
};

export async function fetchLegacyMessagesByUserId(
  userId: string,
): Promise<LegacyMessageListItem[]> {
  const uid = userId?.trim();
  if (!uid) return [];

  const all: LegacyMessageListItem[] = [];
  let nextToken: string | null | undefined = null;

  do {
    const data: any = await graphqlUserPool<ListMessagesResponse>({
      query: listMessagesWithRecipientsAndAttachments,
      variables: {
        filter: { userprofileID: { eq: uid } },
        limit: 1000,
        nextToken,
      },
    });

    const page = data?.listMessages;
    const items = page?.items?.filter(Boolean) ?? [];

    all.push(
      ...items.map((item: any) => ({
        __typename: "Message",
        id: item.id,
        title: item.title ?? null,
        bodySnippet: item.bodySnippet ?? null,
        status: item.status ?? null,
        scheduledDate: item.scheduledDate ?? null,
        endOfLifeTrigger:
          item.endOfLifeTrigger !== undefined ? item.endOfLifeTrigger : null,
        size: item.size !== undefined && item.size !== null ? item.size : null,
        userprofileID: item.userprofileID ?? "",
        Recipients: item.Recipients ?? null,
        Attachments: item.Attachments ?? null,
        createdAt: item.createdAt ?? "",
        updatedAt: item.updatedAt ?? "",
        sentDate: item.sentDate ?? null,
      })),
    );

    nextToken = page?.nextToken ?? null;
  } while (nextToken);
  logToConsole("log", "fetchLegacyMessagesByUserId", all);
  return all;
}

/** ---------------- Detail fetchers (single item with full body) ---------------- */
export async function fetchMessageById(id: string): Promise<Message | null> {
  const mid = (id ?? "").trim();
  if (!mid) return null;

  const data: any = await graphqlUserPool({
    query: getMessageByIdWithBodyHydrated,
    variables: { id: mid },
  });

  const item = data?.getMessageByIdWithBody;
  if (!item) return null;

  return {
    __typename: "Message",
    id: item.id,
    title: item.title ?? null,
    body: item.body ?? "",
    bodySnippet: item.bodySnippet ?? null,
    status: item.status ?? null,
    scheduledDate: item.scheduledDate ?? null,
    endOfLifeTrigger:
      item.endOfLifeTrigger !== undefined ? item.endOfLifeTrigger : null,
    size: item.size !== undefined && item.size !== null ? item.size : null,
    userprofileID: item.userprofileID ?? "",
    Recipients: item.Recipients ?? null,
    Attachments: item.Attachments ?? null,
    createdAt: item.createdAt ?? "",
    updatedAt: item.updatedAt ?? "",
    sentDate: item.sentDate ?? null,
  };
}

export async function fetchDeliveredMessageById(
  id: string,
): Promise<DeliveredMessages | null> {
  const mid = (id ?? "").trim();
  if (!mid) return null;

  const data: any = await graphqlUserPool({
    query: getDeliveredMessageByIdWithBody,
    variables: { id: mid },
  });

  const item = data?.getDeliveredMessageByIdWithBody;
  return item ? mapDeliveredMessage(item) : null;
}

type UpdateDeliveredMessageReadResponse = {
  updateDeliveredMessages: DeliveredMessages | null;
};

export async function markDeliveredMessageAsRead(
  id: string,
): Promise<DeliveredMessages | null> {
  logToConsole("log", "markDeliveredMessageAsRead called with id:", id);

  const mid = (id ?? "").trim();
  if (!mid) {
    logToConsole("warn", "markDeliveredMessageAsRead aborted: empty id");
    return null;
  }

  try {
    logToConsole("log", "markDeliveredMessageAsRead before graphqlUserPool");

    const data: any = await graphqlUserPool<UpdateDeliveredMessageReadResponse>(
      {
        query: updateDeliveredMessages,
        variables: { input: { id: mid, messageRead: true } },
      },
    );

    logToConsole("log", "markDeliveredMessageAsRead response data:", data);

    return data?.updateDeliveredMessages ?? null;
  } catch (error: any) {
    logToConsole(
      "error",
      "markDeliveredMessageAsRead failed:",
      JSON.stringify(error, null, 2),
    );
    console.error("RAW ERROR:", error);
    return null;
  } finally {
    logToConsole("log", "markDeliveredMessageAsRead finished");
  }
}

/** ---------------- Recipients (all pages, includes linked messages) ---------------- */

type ListRecipientsResponse = {
  listRecipients: {
    items: any[];
    nextToken?: string | null;
  };
};

export async function fetchRecipientsByUserId(
  userId: string,
): Promise<Recipient[]> {
  const uid = userId?.trim();
  if (!uid) return [];

  const all: Recipient[] = [];
  let nextToken: string | null | undefined = null;

  do {
    const data: any = await graphqlUserPool<ListRecipientsResponse>({
      query: listRecipientsWithMessages,
      variables: {
        filter: { userprofileID: { eq: uid } },
        nextToken,
      },
    });

    const page: any = data?.listRecipients;
    const items: any[] = page?.items?.filter(Boolean) ?? [];

    all.push(
      ...items.map((item: any) => ({
        __typename: "Recipient" as const,
        id: item.id,
        firstName: item.firstName ?? null,
        lastName: item.lastName ?? null,
        email: item.email ?? null,
        phone: item.phone ?? null,
        phoneType: item.phoneType ?? null,
        sendSMS: item.sendSMS ?? null,
        userprofileID: item.userprofileID ?? "",
        messages: item.messages ?? null,
        createdAt: item.createdAt ?? "",
        updatedAt: item.updatedAt ?? "",
      })),
    );

    nextToken = page?.nextToken ?? null;
  } while (nextToken);

  // Optional: stable picker ordering
  all.sort((a, b) => {
    const al = (a.lastName ?? "").toLowerCase();
    const bl = (b.lastName ?? "").toLowerCase();
    if (al !== bl) return al.localeCompare(bl);
    const af = (a.firstName ?? "").toLowerCase();
    const bf = (b.firstName ?? "").toLowerCase();
    return af.localeCompare(bf);
  });
  logToConsole("log", "fetchRecipientsByUserId", all);
  return all;
}

/** ---------------- Attachments/Files (all pages, includes linked messages) ---------------- */

type ListAttachmentsResponse = {
  listAttachments: {
    items: any[];
    nextToken?: string | null;
  };
};

export async function fetchAttachmentsByUserId(
  userId: string,
): Promise<Attachment[]> {
  const uid = userId?.trim();
  if (!uid) return [];

  const all: Attachment[] = [];
  let nextToken: string | null | undefined = null;

  do {
    const data: any = await graphqlUserPool<ListAttachmentsResponse>({
      query: listAttachmentsWithMessages,
      variables: {
        filter: { userprofileID: { eq: uid } },
        nextToken,
      },
    });

    const page: any = data?.listAttachments;
    const items: any[] = page?.items?.filter(Boolean) ?? [];

    all.push(
      ...items.map((item: any) => ({
        __typename: "Attachment" as const,
        id: item.id,
        name: item.name ?? null,
        description: item.description ?? null,
        type: item.type ?? null,
        status: item.status ?? null,
        size: item.size !== undefined && item.size !== null ? item.size : null,
        s3Key: item.s3Key ?? null,
        uploadDate: item.uploadDate ?? null,
        userprofileID: item.userprofileID ?? "",
        messages: item.messages ?? null,
        createdAt: item.createdAt ?? "",
        updatedAt: item.updatedAt ?? "",
      })),
    );

    nextToken = page?.nextToken ?? null;
  } while (nextToken);

  // Optional: newest first by uploadDate
  all.sort((a, b) => {
    const da = a.uploadDate ? new Date(a.uploadDate).getTime() : 0;
    const db = b.uploadDate ? new Date(b.uploadDate).getTime() : 0;
    return db - da;
  });
  logToConsole("log", "fetchAttachmentsByUserId", all);
  return all;
}

/** ---------------- Delivered / Inherited Messages by email (all pages) ---------------- */

type DeliveredByEmailResponse = {
  deliveredMessagesByRecipientEmail: {
    items: (DeliveredMessageListItem | null)[];
    nextToken?: string | null;
  } | null;
};

type DeliveredBySenderResponse = {
  deliveredMessagesByFromUserProfileId: {
    items: (DeliveredMessages | null)[];
    nextToken?: string | null;
  } | null;
};

function mapDeliveredMessage(item: any): DeliveredMessages {
  return {
    __typename: "DeliveredMessages",
    id: item.id,
    recipientEmail: item.recipientEmail ?? "",
    recipientId: item.recipientId ?? null,
    recipientUserId: item.recipientUserId ?? null,
    deliveredAt: item.deliveredAt ?? null,
    deliveryType: item.deliveryType ?? "",
    messageRead: item.messageRead ?? null,
    readReminderCount: item.readReminderCount ?? null,
    readReminderStatus: item.readReminderStatus ?? null,
    status: item.status ?? "",
    failureReason: item.failureReason ?? null,
    attemptCount: item.attemptCount ?? null,
    lastAttemptAt: item.lastAttemptAt ?? null,
    statusUpdatedAt: item.statusUpdatedAt ?? null,
    fromUserProfileId: item.fromUserProfileId ?? null,
    fromName: item.fromName ?? "",
    fromEmail: item.fromEmail ?? null,
    originalMessageId: item.originalMessageId ?? null,
    title: item.title ?? "",
    body: item.body ?? "",
    bodyIv: item.bodyIv ?? null,
    bodyAuthTag: item.bodyAuthTag ?? null,
    bodyEncryptedDataKey: item.bodyEncryptedDataKey ?? null,
    bodySnippet: item.bodySnippet ?? null,
    hasAttachment: item.hasAttachment ?? false,
    attachmentCount: item.attachmentCount ?? 0,
    createdAt: item.createdAt ?? "",
    updatedAt: item.updatedAt ?? "",
  };
}

/**
 * The sender's own delivery records, every status (QUEUED/FAILED rows have no deliveredAt yet).
 * Reads the byFromUserProfileId index: listDeliveredMessages is scoped to the caller as *recipient*
 * server-side, so filtering it by fromUserProfileId returned the caller's received messages instead.
 */
export async function fetchDeliveredMessagesBySender(
  userId: string,
): Promise<DeliveredMessages[]> {
  const uid = (userId ?? "").trim();
  if (!uid) return [];

  const all: DeliveredMessages[] = [];
  let nextToken: string | null | undefined = null;

  do {
    const data: any = await graphqlUserPool<DeliveredBySenderResponse>({
      query: deliveredMessagesByFromUserProfileIdDocument,
      variables: {
        fromUserProfileId: uid,
        limit: 1000,
        nextToken,
      },
    });

    const page = data?.deliveredMessagesByFromUserProfileId;
    const items = page?.items?.filter(Boolean) ?? [];

    all.push(...items.map((item: any) => mapDeliveredMessage(item)));
    nextToken = page?.nextToken ?? null;
  } while (nextToken);

  all.sort((a, b) => {
    const da = a.deliveredAt ? new Date(a.deliveredAt).getTime() : 0;
    const db = b.deliveredAt ? new Date(b.deliveredAt).getTime() : 0;
    return db - da;
  });

  logToConsole("log", "fetchDeliveredMessagesBySender", all);
  return all;
}

export async function fetchDeliveredMessagesByRecipientEmail(
  recipientEmail: string,
): Promise<DeliveredMessageListItem[]> {
  const email = (recipientEmail ?? "").trim().toLowerCase();
  if (!email) return [];

  const all: DeliveredMessageListItem[] = [];
  let nextToken: string | null | undefined = null;

  do {
    const data: any = await graphqlUserPool<DeliveredByEmailResponse>({
      query: deliveredMessagesByRecipientEmail,
      variables: {
        recipientEmail: email,
        sortDirection: "DESC" as ModelSortDirection,
        limit: 1000,
        nextToken,
      },
    });

    const page: any = data?.deliveredMessagesByRecipientEmail;
    const items: any[] = page?.items?.filter(Boolean) ?? [];

    all.push(
      ...items.map((item: any) => {
        const mapped = mapDeliveredMessage(item);
        const { body: _body, ...listItem } = mapped;
        return listItem;
      }),
    );

    nextToken = page?.nextToken ?? null;
  } while (nextToken);

  const deliveredOnly = all.filter(
    (m) => (m?.status ?? "").toUpperCase() === "DELIVERED",
  );

  deliveredOnly.sort((a, b) => {
    const da = a.deliveredAt ? new Date(a.deliveredAt).getTime() : 0;
    const db = b.deliveredAt ? new Date(b.deliveredAt).getTime() : 0;
    return db - da;
  });
  logToConsole("log", "fetchDeliveredMessagesByRecipientEmail", deliveredOnly);
  return deliveredOnly;
}

/** ---------------- Delivered / Inherited Message Attachments by message id ---------------- */
type AttachmentsByDeliveredMessageResponse = {
  attachmentsByDeliveredMessageId: {
    items: (DeliveredMessagesAttachments | null)[];
    nextToken?: string | null;
  } | null;
};

export async function fetchDeliveredMessageAttachmentsByDeliveredMessageId(
  deliveredMessageId: string,
): Promise<DeliveredMessagesAttachments[]> {
  const messageId = (deliveredMessageId ?? "").trim();
  if (!messageId) return [];

  const all: DeliveredMessagesAttachments[] = [];
  let nextToken: string | null | undefined = null;

  do {
    const data: any =
      await graphqlUserPool<AttachmentsByDeliveredMessageResponse>({
        query: attachmentsByDeliveredMessageId,
        variables: {
          deliveredMessageId: messageId,
          limit: 1000,
          nextToken,
        },
      });

    const page = data?.attachmentsByDeliveredMessageId;
    const items = page?.items?.filter(Boolean) ?? [];

    all.push(
      ...items.map((item: any) => ({
        __typename: "DeliveredMessagesAttachments" as const,
        id: item.id,
        deliveredMessageId: item.deliveredMessageId,
        recipientEmail: item.recipientEmail,
        fileName: item.fileName,
        fileType: item.fileType ?? null,
        fileSize: item.fileSize ?? null,
        s3Key: item.s3Key,
        uploadedAt: item.uploadedAt ?? null,
        createdAt: item.createdAt ?? "",
        updatedAt: item.updatedAt ?? "",
      })),
    );

    nextToken = page?.nextToken ?? null;
  } while (nextToken);
  logToConsole(
    "log",
    "fetchDeliveredMessageAttachmentsByDeliveredMessageId",
    all,
  );
  return all;
}

type DeliveredMessageActivityLogArgs = {
  actionType: string;
  entityId: string;
  originalMessageId?: string | null;
  deliveredMessageId?: string | null;
  recipientId?: string | null;
  details?: Record<string, unknown>;
  functionName: string;
};

type ChangeLogValue =
  | string
  | number
  | boolean
  | readonly string[]
  | Record<string, unknown>
  | null;

export type ChangeLogEntryInput = {
  requestId: string;
  entityType: ChangeLogEntityType;
  entityId: string;
  changeType: ChangeLogChangeType;
  fieldName?: string | null;
  oldValue?: ChangeLogValue;
  newValue?: ChangeLogValue;
  metadata?: Record<string, unknown> | null;
  userId: string;
};

function createExecutionId() {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
}

function serializeChangeLogValue(value: ChangeLogValue | undefined) {
  if (value == null) return null;
  return JSON.stringify(value);
}

export function createChangeRequestId() {
  return createExecutionId();
}

export async function logChange({
  requestId,
  entityType,
  entityId,
  changeType,
  fieldName = null,
  oldValue = null,
  newValue = null,
  metadata = null,
  userId,
}: ChangeLogEntryInput): Promise<void> {
  await graphqlUserPool<CreateChangeLogMutation>({
    query: createChangeLog,
    variables: {
      input: {
        requestId,
        changedById: userId,
        userId,
        entityType,
        entityId,
        changeType,
        fieldName,
        oldValue: serializeChangeLogValue(oldValue),
        newValue: serializeChangeLogValue(newValue),
        metadata: metadata ? JSON.stringify(metadata) : null,
        dateTime: new Date().toISOString(),
      },
    } satisfies CreateChangeLogMutationVariables,
  });
}

export async function logChanges(
  entries: ChangeLogEntryInput[],
): Promise<void> {
  if (entries.length === 0) return;
  logToConsole("log", "Logging changes:", entries);
  const results = await Promise.allSettled(
    entries.map((entry) => logChange(entry)),
  );
  const failed = results.find((result) => result.status === "rejected");
  if (failed) {
    logToConsole("error", "ChangeLog write failed:", failed.reason);
  }
}

export async function createDeliveredMessageActivityLog({
  actionType,
  entityId,
  originalMessageId = null,
  deliveredMessageId = null,
  recipientId = null,
  details,
  functionName,
}: DeliveredMessageActivityLogArgs): Promise<void> {
  const executionId = createExecutionId();

  await graphqlUserPool<CreateMessageDeliveryLogMutation>({
    query: createMessageDeliveryLog,
    variables: {
      input: {
        executionId,
        actionType,
        entityType: "DeliveredMessages",
        entityId,
        originalMessageId,
        deliveredMessageId,
        recipientId,
        timestamp: new Date().toISOString(),
        functionName,
        details: details ? JSON.stringify(details) : null,
      },
    } satisfies CreateMessageDeliveryLogMutationVariables,
  });
}

export async function requestDeliveredMessageAttachmentDownloads(
  deliveredMessageId: string,
  attachmentIds?: string[],
): Promise<DeliveredAttachmentDownloadPayload> {
  const messageId = deliveredMessageId.trim();

  const data =
    await graphqlUserPool<DownloadDeliveredMessageAttachmentsMutation>({
      query: downloadDeliveredMessageAttachments,
      variables: {
        deliveredMessageId: messageId,
        attachmentIds:
          attachmentIds && attachmentIds.length > 0 ? attachmentIds : null,
      } satisfies DownloadDeliveredMessageAttachmentsMutationVariables,
    });

  return data.downloadDeliveredMessageAttachments;
}
